// frontend/src/constants/assetOptions.js
/**
 * Shared option catalogs for asset forms (Add / Edit Asset modals).
 *
 * Two kinds of helpers:
 *   - *_OPTIONS  → closed sets for <FormSelect> ({ value, label })
 *   - *_SUGGESTIONS → open lists for <datalist> comboboxes (string[]).
 *     The user can still type a value that isn't in the list.
 *
 * Keep these in sync with backend choices where applicable:
 *   - CONDITION_OPTIONS  ↔ Asset.Condition (asset_inventory/models.py)
 *   - OWNERSHIP_OPTIONS  ↔ Asset.OwnershipType
 */

// --- Closed enums (mirror backend Asset.Condition / Asset.OwnershipType) ---

export const CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
  { value: 'faulty', label: 'Faulty' },
];

export const OWNERSHIP_OPTIONS = [
  { value: 'owned', label: 'Owned' },
  { value: 'leased', label: 'Leased' },
];

// --- Specification dropdowns (standardised hardware values) ---

export const RAM_GB_OPTIONS = [4, 8, 16, 32, 64, 128].map((n) => ({
  value: String(n),
  label: `${n} GB`,
}));

export const STORAGE_GB_OPTIONS = [128, 256, 512, 1024, 2048, 4096].map((n) => ({
  value: String(n),
  label: n >= 1024 ? `${n / 1024} TB` : `${n} GB`,
}));

export const RESOLUTION_OPTIONS = [
  { value: '1366x768', label: '1366 × 768 (HD)' },
  { value: '1920x1080', label: '1920 × 1080 (FHD)' },
  { value: '2560x1440', label: '2560 × 1440 (QHD)' },
  { value: '3440x1440', label: '3440 × 1440 (UWQHD)' },
  { value: '3840x2160', label: '3840 × 2160 (4K UHD)' },
];

export const MONITOR_SIZE_OPTIONS = [21.5, 23.8, 24, 27, 32, 34].map((n) => ({
  value: String(n),
  label: `${n}"`,
}));

// --- Open suggestion lists (datalist comboboxes, free text allowed) ---

export const BRAND_SUGGESTIONS = [
  'Dell', 'HP', 'Lenovo', 'Apple', 'Microsoft', 'ASUS', 'Acer', 'MSI',
  'Samsung', 'LG', 'Logitech', 'Cisco', 'Toshiba', 'Sony', 'Huawei', 'Xiaomi',
];

export const CPU_SUGGESTIONS = [
  'Intel Core i3', 'Intel Core i5', 'Intel Core i7', 'Intel Core i9',
  'Intel Core Ultra 5', 'Intel Core Ultra 7', 'Intel Core Ultra 9',
  'AMD Ryzen 3', 'AMD Ryzen 5', 'AMD Ryzen 7', 'AMD Ryzen 9',
  'Apple M1', 'Apple M2', 'Apple M3', 'Apple M4',
];

export const OS_VERSION_SUGGESTIONS = [
  'Windows 11 Pro', 'Windows 11 Home', 'Windows 10 Pro', 'Windows 10 Home',
  'macOS Sequoia', 'macOS Sonoma', 'macOS Ventura',
  'Ubuntu 24.04 LTS', 'Ubuntu 22.04 LTS', 'ChromeOS',
];

export const WARRANTY_PROVIDER_SUGGESTIONS = [
  'Manufacturer Warranty', 'Dell ProSupport', 'HP Care Pack',
  'Lenovo Premier Support', 'AppleCare', 'ASUS Premium Care', 'On-site Service',
];

/**
 * Per-key intelligence for AssetType.custom_fields_schema properties.
 * Drives how the "Specifications" section renders each field:
 *   kind: 'select'   → closed <FormSelect> using `options`
 *   kind: 'combobox' → free-text <datalist> input using `suggestions`
 * Schema-defined `enum` always takes precedence over this map.
 */
export const SPEC_FIELD_CONFIG = {
  ram_gb: { kind: 'select', options: RAM_GB_OPTIONS },
  storage_gb: { kind: 'select', options: STORAGE_GB_OPTIONS },
  resolution: { kind: 'select', options: RESOLUTION_OPTIONS },
  size_inches: { kind: 'select', options: MONITOR_SIZE_OPTIONS },
  cpu: { kind: 'combobox', suggestions: CPU_SUGGESTIONS },
  os_version: { kind: 'combobox', suggestions: OS_VERSION_SUGGESTIONS },
};
